import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Icon } from './ui';

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
}

export default function CropFotoModal({ imageSrc, onCancel, onSave }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleSave() {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      onSave(blob);
    } catch {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 400,
      display: 'flex', flexDirection: 'column',
      background: '#000',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', flexShrink: 0,
      }}>
        <button onClick={onCancel} style={{
          background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8,
          width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: '#fff',
        }}>
          <Icon name="close" size={18}/>
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: -0.2 }}>
          Ajustar foto
        </span>
        <div style={{ width: 36 }}/>
      </div>

      {/* Cropper */}
      <div style={{ position: 'relative', flex: 1 }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          style={{
            containerStyle: { background: '#000' },
            cropAreaStyle: { border: '2px solid rgba(255,255,255,0.7)', boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' },
          }}
        />
      </div>

      {/* Zoom */}
      <div style={{
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(0,0,0,0.7)', flexShrink: 0,
      }}>
        <Icon name="user" size={13} style={{ color: 'rgba(255,255,255,0.4)' }}/>
        <input
          type="range" min={1} max={3} step={0.02}
          value={zoom}
          onChange={e => setZoom(Number(e.target.value))}
          style={{ flex: 1, accentColor: '#3eb8b1', height: 4, cursor: 'pointer' }}
        />
        <Icon name="user" size={20} style={{ color: 'rgba(255,255,255,0.4)' }}/>
      </div>

      {/* Actions */}
      <div style={{
        padding: '12px 16px 32px',
        display: 'flex', gap: 10,
        background: '#111', flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1, height: 46, background: 'rgba(255,255,255,0.10)',
            color: '#fff', border: 'none', borderRadius: 12,
            fontFamily: 'inherit', fontSize: 14, fontWeight: 540, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 2, height: 46,
            background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 12,
            fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Processando…' : 'Usar esta foto'}
        </button>
      </div>
    </div>
  );
}
